import React, { useState, useEffect, useRef } from "react";
import colors from "../colors";
import { getHistoricalData } from "../services/stockApi";

const PortfolioGraph = ({
  symbol = "^GSPC",
  title = "S&P 500",
  height = 500,
  width = 800,
  margin = 20,
  leftMargin = 2,
  showTimeframes = true,
  showTitle = true,
  showPrice = true,
  showBackground = true,
  showDisplayMode = true,
}) => {
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState("1mo");
  const [containerWidth, setContainerWidth] = useState(800);
  const [displayMode, setDisplayMode] = useState("value");
  const [hoverData, setHoverData] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [priceChangePercent, setPriceChangePercent] = useState(0);
  const [buttonOffset, setButtonOffset] = useState(0);
  const containerRef = useRef(null);

  // Calculate actual width based on prop type
  const actualWidth =
    typeof width === "string" && width.includes("%") ? containerWidth : width;

  // Handle mouse hover events
  const handleMouseMove = (event) => {
    if (!graphData || graphData.length === 0) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;

    // Check if mouse is within the graph area
    if (mouseX < leftMargin || mouseX > actualWidth - margin) {
      setHoverData(null);
      return;
    }

    // Calculate which data point is closest to the mouse
    const graphWidth = actualWidth - leftMargin - margin;
    const xStep = graphWidth / (graphData.length - 1);
    const dataIndex = Math.round((mouseX - leftMargin) / xStep);
    const clampedIndex = Math.max(0, Math.min(dataIndex, graphData.length - 1));

    const dataPoint = graphData[clampedIndex];

    setHoverData({
      x: mouseX,
      dataPoint,
      index: clampedIndex,
    });
  };

  const handleMouseLeave = () => {
    setHoverData(null);
  };

  const handleButtonContainerMouseMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const containerWidth = rect.width;
    
    // Calculate continuous offset based on mouse position
    const maxOffset = Math.max(0, timeframes.length - 5); // Maximum possible offset
    const progress = mouseX / containerWidth; // 0 to 1
    const newOffset = progress * maxOffset; // Remove Math.round for continuous movement
    
    setButtonOffset(Math.max(0, Math.min(maxOffset, newOffset)));
  };

  // Calculate the transform for smooth sliding
  const getButtonTransform = () => {
    const buttonWidth = 60; // Width of each button (reduced)
    const gap = 8; // Gap between buttons
    const maxOffset = Math.max(0, timeframes.length - 5);
    
    if (maxOffset === 0) return "translateX(20px)"; // Add 20px gap for far left
    
    // Simple calculation: move by exactly button width + gap with left padding
    const leftPadding = 20; // Gap for far left position
    const translateX = leftPadding - buttonOffset * (buttonWidth + gap);
    
    return `translateX(${translateX}px)`;
  };

  useEffect(() => {
    if (
      typeof width === "string" &&
      width.includes("%") &&
      containerRef.current
    ) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          setContainerWidth(entry.contentRect.width);
        }
      });

      resizeObserver.observe(containerRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [width]);

  const timeframes = [
    { label: "1D", value: "1d" },
    { label: "1W", value: "5d" },
    { label: "1M", value: "1mo" },
    { label: "3M", value: "3mo" },
    { label: "YTD", value: "ytd" },
    { label: "1Y", value: "1y" },
    { label: "5Y", value: "5y" },
    { label: "MAX", value: "max" },
  ];

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const data = await getHistoricalData(symbol, timeframe);
        const formatted = data.map((d) => ({
          date: d.date,
          value: d.price,
        }));
        setGraphData(formatted);

        if (formatted.length > 0) {
          setCurrentPrice(formatted[formatted.length - 1].value);
          const firstValue = formatted[0].value;
          const lastValue = formatted[formatted.length - 1].value;
          const percentChange = ((lastValue - firstValue) / firstValue) * 100;
          setPriceChangePercent(percentChange);
        } else {
          setCurrentPrice(null);
          setPriceChangePercent(0);
        }
      } catch (e) {
        setGraphData([]);
        setCurrentPrice(null);
        setPriceChangePercent(0);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [symbol, timeframe]);

  // Determine color based on price change over the timeframe
  const isPositive = priceChangePercent >= 0;

  const renderGraph = () => {
    if (loading) return null;
    if (!graphData || graphData.length === 0) return null;

    const values = graphData.map((d) => d.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue;
    const padding = range * 0.1;

    // Calculate percent data if in percent mode
    let percentData = [];
    let percentMin = 0,
      percentMax = 0;
    if (displayMode === "percent" && graphData.length > 0) {
      const base = graphData[0].value;
      percentData = graphData.map((point) => ({
        date: point.date,
        percent: ((point.value - base) / base) * 100,
      }));
      percentMin = Math.min(...percentData.map((d) => d.percent));
      percentMax = Math.max(...percentData.map((d) => d.percent));
      // Always include 0% in the range
      if (percentMin > 0) percentMin = 0;
      if (percentMax < 0) percentMax = 0;
      // Add a little padding for visual clarity
      const pad = (percentMax - percentMin) * 0.08;
      percentMin -= pad;
      percentMax += pad;
    }

    const graphWidth = actualWidth - leftMargin - margin; // Account for left and right margins
    const graphHeight = height - 200; // Account for title, price, and padding
    const lineEndOffset = 25; // Make the line end 25px earlier
    const xStep = (graphWidth - lineEndOffset) / (graphData.length - 1);

    // Add extra padding for labels to prevent cutoff
    const labelPadding = 30; // Extra space for labels
    const effectiveGraphHeight = graphHeight - labelPadding * 2; // Reduce graph area for label space

    // Use percent or value data for calculations
    const dataToUse =
      displayMode === "percent" && percentData.length > 0
        ? percentData
        : graphData;
    const min = displayMode === "percent" ? percentMin : minValue - padding;
    const max = displayMode === "percent" ? percentMax : maxValue + padding;
    const dataRange = max - min;
    const yStep = effectiveGraphHeight / dataRange;

    // --- NEW: For percent mode, split background into green/red polygons with gradients ---
    let backgroundPolygons = null;
    let backgroundGradients = [];
    if (displayMode === "percent" && percentData.length > 1) {
      function interpolateZeroCross(p1, p2) {
        const x1 = leftMargin + p1.idx * xStep;
        const x2 = leftMargin + p2.idx * xStep;
        const t = (0 - p1.percent) / (p2.percent - p1.percent);
        return {
          x: x1 + (x2 - x1) * t,
        };
      }
      let segments = [];
      let current = [];
      let currentSign = percentData[0].percent >= 0 ? 1 : -1;
      for (let i = 0; i < percentData.length; i++) {
        const d = percentData[i];
        const sign = d.percent >= 0 ? 1 : -1;
        current.push({ ...d, idx: i });
        if (i < percentData.length - 1) {
          const next = percentData[i + 1];
          const nextSign = next.percent >= 0 ? 1 : -1;
          if (sign !== nextSign) {
            const cross = interpolateZeroCross(
              { ...d, idx: i },
              { ...next, idx: i + 1 }
            );
            current.push({ percent: 0, idx: null, x: cross.x });
            segments.push({
              points: [...current],
              sign: currentSign,
            });
            current = [
              { percent: 0, idx: null, x: cross.x },
            ];
            currentSign = nextSign;
          }
        }
      }
      if (current.length > 1) {
        segments.push({ points: current, sign: currentSign });
      }
      backgroundPolygons = segments.map((seg, idx) => {
        const polyPoints = seg.points.map((pt) => {
          if (pt.idx !== null) {
            const x = leftMargin + pt.idx * xStep;
            const y = graphHeight - labelPadding - (pt.percent - min) * yStep;
            return `${x},${y}`;
          } else {
            // Interpolated crossing
            const y = graphHeight - labelPadding - (0 - min) * yStep;
            return `${pt.x},${y}`;
          }
        });
        const firstX = seg.points[0].idx !== null
          ? leftMargin + seg.points[0].idx * xStep
          : seg.points[0].x;
        const lastX = seg.points[seg.points.length - 1].idx !== null
          ? leftMargin + seg.points[seg.points.length - 1].idx * xStep
          : seg.points[seg.points.length - 1].x;
        const zeroY = graphHeight - labelPadding - (0 - min) * yStep;
        const bottomPoints = [
          `${lastX},${zeroY}`,
          `${firstX},${zeroY}`,
        ];
        // --- Gradient for this segment ---
        const gradId = `bg-grad-segment-${idx}-${symbol}-${timeframe}`;
        backgroundGradients.push({
          id: gradId,
          color: seg.sign > 0 ? "#22C55E" : "#EF4444",
        });
        return {
          points: [...polyPoints, ...bottomPoints].join(" "),
          gradId,
          key: `bg-segment-${idx}`,
        };
      });
    }
    // --- END NEW ---

    // Note: firstValue and lastValue are used for gradient direction calculation
    // but the actual gradient is determined by the isPositive variable above

    // Create gradient for the line
    const gradientId = `gradient-${symbol}-${timeframe}`;

    // Generate points for the polyline
    const points = dataToUse
      .map((d, i) => {
        const x = leftMargin + i * xStep; // Add left margin offset
        const value = displayMode === "percent" ? d.percent : d.value;
        const y = graphHeight - labelPadding - (value - min) * yStep;
        return `${x},${y}`;
      })
      .join(" ");

    // Generate points for the background that ends exactly with the graph line
    const lastPoint = dataToUse[dataToUse.length - 1];
    const lastX = leftMargin + (dataToUse.length - 1) * xStep;
    const lastValue = displayMode === "percent" ? lastPoint.percent : lastPoint.value;
    const lastY = graphHeight - labelPadding - (lastValue - min) * yStep;
    
    const backgroundPoints = dataToUse
      .map((d, i) => {
        const x = leftMargin + i * xStep; // Add left margin offset
        const value = displayMode === "percent" ? d.percent : d.value;
        const y = graphHeight - labelPadding - (value - min) * yStep;
        return `${x},${y}`;
      })
      .join(" ") + ` ${lastX},${graphHeight - labelPadding}`;

    // Calculate grid lines
    const gridLines = [];
    let labelCount = 5;

    if (displayMode === "percent") {
      // Simplified and more robust label generation for percent mode
      const maxLabels = 12;
      const rawMin = percentMin;
      let rawMax = percentMax;

      // Define nice step increments
      const niceSteps = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100];

      // Find the best step size
      let bestStep = 1;
      let bestLabelCount = maxLabels + 1;

      for (const step of niceSteps) {
        // Calculate how many labels this step would create
        let axisMin = Math.floor(rawMin / step) * step;
        let axisMax = Math.ceil(rawMax / step) * step;

        // Ensure 0 is included if in range
        if (axisMin < 0 && axisMax > 0) {
          axisMin = Math.min(axisMin, 0);
          axisMax = Math.max(axisMax, 0);
        }

        // Ensure axisMax is at least 0
        if (axisMax < 0) axisMax = 0;

        // Count labels
        let labelCount = 0;
        for (let v = axisMin; v <= axisMax + step / 2; v += step) {
          labelCount++;
        }

        // If this step gives us a reasonable number of labels, use it
        if (labelCount <= maxLabels && labelCount >= 3) {
          bestStep = step;
          bestLabelCount = labelCount;
          break;
        }

        // Keep track of the best option so far
        if (labelCount < bestLabelCount && labelCount >= 3) {
          bestStep = step;
          bestLabelCount = labelCount;
        }
      }

      // Generate labels with the best step
      let axisMin = Math.floor(rawMin / bestStep) * bestStep;
      let axisMax = Math.ceil(rawMax / bestStep) * bestStep;

      // Ensure 0 is included if in range
      if (axisMin < 0 && axisMax > 0) {
        axisMin = Math.min(axisMin, 0);
        axisMax = Math.max(axisMax, 0);
      }
      // Ensure axisMax is at least 0
      if (axisMax < 0) axisMax = 0;

      // Generate the arithmetic sequence
      const gridVals = [];
      for (let v = axisMin; v <= axisMax + bestStep / 2; v += bestStep) {
        const rounded = Math.round(v * 100) / 100; // Round to 2 decimal places
        gridVals.push(rounded);
      }

      // Remove duplicates and sort
      const uniqueGridVals = Array.from(new Set(gridVals)).sort(
        (a, b) => a - b
      );

      // Create grid lines
      uniqueGridVals.forEach((value) => {
        const y = graphHeight - labelPadding - (value - min) * yStep;
        gridLines.push({ value, y });
      });
      // Ensure last label is at least the actual max and positive
      const actualMax = Math.max(...percentData.map((d) => d.percent));
      if (gridLines.length > 0 && gridLines[gridLines.length - 1].value < actualMax && actualMax > 0) {
        const y = graphHeight - labelPadding - (actualMax - min) * yStep;
        gridLines.push({ value: actualMax, y });
      }
    } else {
      // For value mode, use simple division
      for (let i = 0; i <= labelCount; i++) {
        const value = min + (dataRange * i) / labelCount;
        gridLines.push({ value, y: graphHeight - labelPadding - (value - min) * yStep });
      }
      // Ensure last label is positive
      if (gridLines.length > 0 && gridLines[gridLines.length - 1].value < 0) {
        const step = (gridLines[1].value - gridLines[0].value);
        const lastY = graphHeight - labelPadding - (0 - min) * yStep;
        gridLines[gridLines.length - 1] = { value: 0, y: lastY };
      }
      // Ensure last label is at least the actual max and positive
      const actualMax = Math.max(...graphData.map((d) => d.value));
      if (gridLines.length > 0 && gridLines[gridLines.length - 1].value < actualMax && actualMax > 0) {
        const y = graphHeight - labelPadding - (actualMax - min) * yStep;
        gridLines.push({ value: actualMax, y });
      }
    }

    // --- X-Axis Label Formatting Helper ---
    function formatXAxisLabel(dateStr, idx, total, timeframe) {
      // Try to parse date/time
      let d = new Date(dateStr);
      if (timeframe === "1d" || timeframe === "5d") {
        // Show hour for 1D, 5D
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else if (timeframe === "1mo" || timeframe === "3mo" || timeframe === "ytd") {
        // Show day/month
        return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
      } else if (timeframe === "1y" || timeframe === "5y") {
        // Show month/year
        return d.toLocaleDateString([], { month: 'short', year: '2-digit' });
      } else if (timeframe === "max") {
        // Show year
        return d.getFullYear();
      } else {
        // Default: day/month
        return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
      }
    }

    // --- X-Axis Labels Calculation ---
    let xAxisLabels = [];
    if (graphData && graphData.length > 1) {
      // Choose a reasonable number of labels (max 8, min 2)
      const maxLabels = 8;
      const minLabels = 2;
      let labelCount = Math.min(maxLabels, Math.max(minLabels, Math.floor(graphData.length / 10)));
      if (graphData.length <= maxLabels) labelCount = graphData.length;
      for (let i = 0; i < labelCount; i++) {
        const idx = Math.round(i * (graphData.length - 1) / (labelCount - 1));
        const d = graphData[idx];
        const x = leftMargin + idx * xStep;
        xAxisLabels.push({
          x,
          label: formatXAxisLabel(d.date, idx, graphData.length, timeframe),
          idx,
        });
      }
    }

    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          margin: 0,
          padding: 0,
          overflow: "visible",
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${actualWidth} ${graphHeight}`}
          style={{
            overflow: "visible",
            margin: 0,
            padding: 0,
            cursor: "crosshair",
          }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {/* Define gradient */}
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop
                offset="0%"
                stopColor={isPositive ? colors.success : colors.error}
              />
              <stop
                offset="100%"
                stopColor={isPositive ? colors.success : colors.error}
              />
            </linearGradient>

            {/* Gradient for percent mode with color change at 0% */}
            {displayMode === "percent" && (
              <linearGradient
                id={`percentGradient-${symbol}-${timeframe}`}
                x1="0"
                y1="0"
                x2="0"
                y2={graphHeight}
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor={colors.success} />
                {(() => {
                  // Calculate the y-position of 0% in SVG coordinates
                  const zeroY = graphHeight - labelPadding - (0 - min) * yStep;
                  const offset = (zeroY / graphHeight) * 100;
                  if (offset > 0 && offset < 100) {
                    return (
                      <>
                        <stop
                          offset={`${offset}%`}
                          stopColor={colors.success}
                        />
                        <stop
                          offset={`${offset}%`}
                          stopColor={colors.error}
                        />
                      </>
                    );
                  }
                  return null;
                })()}
                <stop offset="100%" stopColor={colors.error} />
              </linearGradient>
            )}

            {/* Background gradient that blends out */}
            <linearGradient
              id={`backgroundGradient-${symbol}-${timeframe}`}
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
              gradientUnits="userSpaceOnUse"
            >
              {displayMode === "percent" ? (
                // For percentage mode, use the exact same gradient as the line
                <>
                  <stop offset="0%" stopColor="rgba(34, 197, 94, 0.4)" />
                  {(() => {
                    const zeroY = graphHeight - labelPadding - (0 - min) * yStep;
                    const zeroPercent = (zeroY / graphHeight) * 100;
                    if (zeroPercent > 0 && zeroPercent < 100) {
                      return (
                        <>
                          <stop
                            offset={`${zeroPercent}%`}
                            stopColor="rgba(34, 197, 94, 0.4)"
                          />
                          <stop
                            offset={`${zeroPercent}%`}
                            stopColor="rgba(239, 68, 68, 0.4)"
                          />
                        </>
                      );
                    }
                    return null;
                  })()}
                  <stop offset="100%" stopColor="rgba(239, 68, 68, 0)" />
                </>
              ) : (
                // For value mode, use the original logic
                <>
                  <stop
                    offset="0%"
                    stopColor={isPositive ? "rgba(34, 197, 94, 0.4)" : "rgba(239, 68, 68, 0.4)"}
                  />
                  <stop
                    offset="50%"
                    stopColor={isPositive ? "rgba(34, 197, 94, 0.15)" : "rgba(239, 68, 68, 0.15)"}
                  />
                  <stop
                    offset="100%"
                    stopColor={isPositive ? "rgba(34, 197, 94, 0)" : "rgba(239, 68, 68, 0)"}
                  />
                </>
              )}
            </linearGradient>

            {/* --- NEW: Render dynamic gradients for each background polygon segment --- */}
            {backgroundGradients && backgroundGradients.map((g, i) => (
              g.color === "#22C55E" ? (
                // Green: strong at graph line, fade out at bottom
                <linearGradient
                  key={g.id}
                  id={g.id}
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor={g.color} stopOpacity="0.4" />
                  <stop offset="80%" stopColor={g.color} stopOpacity="0.15" />
                  <stop offset="100%" stopColor={g.color} stopOpacity="0" />
                </linearGradient>
              ) : (
                // Red: almost transparent at graph line (top), strongest at bottom
                <linearGradient
                  key={g.id}
                  id={g.id}
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor={g.color} stopOpacity="0" />
                  <stop offset="20%" stopColor={g.color} stopOpacity="0.15" />
                  <stop offset="100%" stopColor={g.color} stopOpacity="0.4" />
                </linearGradient>
              )
            ))}
            {/* --- END NEW --- */}
          </defs>

          {/* Grid lines */}
          {gridLines.map((line, i) => (
            <g key={i}>
              <line
                x1={leftMargin}
                y1={line.y}
                x2={actualWidth - margin}
                y2={line.y}
                stroke={
                  displayMode === "percent" && Math.abs(line.value) < 0.01
                    ? "#3B82F6"
                    : Math.abs(line.value) < 0.01
                    ? "#374151"
                    : "rgba(255,255,255,0.1)"
                }
                strokeWidth={
                  displayMode === "percent" && Math.abs(line.value) < 0.01
                    ? 2
                    : Math.abs(line.value) < 0.01
                    ? 2
                    : 1
                }
                strokeDasharray={
                  displayMode === "percent" && Math.abs(line.value) < 0.01
                    ? "none"
                    : Math.abs(line.value) < 0.01
                    ? "none"
                    : "5,5"
                }
              />
              <text
                x={actualWidth - margin - 5}
                y={line.y + 4}
                fill={
                  displayMode === "percent" && Math.abs(line.value) < 0.01
                    ? "#3B82F6"
                    : Math.abs(line.value) < 0.01
                    ? "#3B82F6"
                    : "#9CA3AF"
                }
                fontSize="12"
                fontWeight={
                  displayMode === "percent" && Math.abs(line.value) < 0.01
                    ? "700"
                    : Math.abs(line.value) < 0.01
                    ? "700"
                    : "600"
                }
                dominantBaseline="middle"
              >
                {displayMode === "percent"
                  ? `${line.value >= 0 ? "+" : ""}${line.value.toFixed(
                      line.value === 0 ? 0 : 2
                    )}%`
                  : line.value.toFixed(2)}
              </text>
            </g>
          ))}

          {/* 0% line for percent mode */}
          {displayMode === "percent" &&
            (() => {
              const zeroY = graphHeight - labelPadding - (0 - min) * yStep;
              return (
                <line
                  x1={leftMargin}
                  y1={zeroY}
                  x2={actualWidth - margin}
                  y2={zeroY}
                  stroke="#3B82F6"
                  strokeWidth="1.5"
                  strokeDasharray="none"
                />
              );
            })()}

          {/* --- NEW: Render split background polygons in percent mode with gradients --- */}
          {displayMode === "percent" && backgroundPolygons &&
            backgroundPolygons.map((poly) => (
              <polygon
                key={poly.key}
                points={poly.points}
                fill={`url(#${poly.gradId})`}
                style={{ pointerEvents: "none" }}
              />
            ))}
          {/* --- END NEW --- */}

          {/* Background area below graph line (old, for value mode) */}
          {displayMode !== "percent" && (
            <polygon
              points={`${leftMargin},${graphHeight - labelPadding} ${backgroundPoints} ${actualWidth - margin},${graphHeight - labelPadding}`}
              fill={`url(#backgroundGradient-${symbol}-${timeframe})`}
            />
          )}

          {/* Graph line */}
          <polyline
            points={points}
            fill="none"
            stroke={
              displayMode === "percent"
                ? `url(#percentGradient-${symbol}-${timeframe})`
                : `url(#${gradientId})`
            }
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Hover vertical line */}
          {hoverData && (
            <>
              <line
                x1={hoverData.x}
                y1={labelPadding}
                x2={hoverData.x}
                y2={graphHeight - labelPadding}
                stroke="#3B82F6"
                strokeWidth="2"
                strokeDasharray="5,5"
                opacity="0.8"
              />

              {/* Hover tooltip */}
              <rect
                x={hoverData.x + 10}
                y={labelPadding + 10}
                width="120"
                height="60"
                fill="rgba(0, 0, 0, 0.8)"
                stroke="#3B82F6"
                strokeWidth="1"
                rx="8"
                opacity="0.95"
              />
              <text
                x={hoverData.x + 20}
                y={labelPadding + 30}
                fill="#ffffff"
                fontSize="12"
                fontWeight="600"
              >
                {hoverData.dataPoint.date}
              </text>
              <text
                x={hoverData.x + 20}
                y={labelPadding + 50}
                fill="#ffffff"
                fontSize="14"
                fontWeight="700"
              >
                {displayMode === "percent"
                  ? `${(
                      ((hoverData.dataPoint.value - graphData[0].value) /
                        graphData[0].value) *
                      100
                    ).toFixed(2)}%`
                  : `$${hoverData.dataPoint.value.toFixed(2)}`}
              </text>
            </>
          )}

          {/* --- X-Axis Labels at the Bottom --- */}
          {xAxisLabels.map((lbl, i) => (
            <text
              key={`x-label-${i}`}
              x={lbl.x}
              y={graphHeight - labelPadding + 22}
              fill="#9CA3AF"
              fontSize="13"
              fontWeight="500"
              textAnchor="middle"
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              {lbl.label}
            </text>
          ))}
          {/* --- END X-Axis Labels --- */}
        </svg>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="card"
      style={{
        position: "relative",
        width: typeof width === "string" ? width : `${width}px`,
        height: height,
        ...(showBackground && {
          background: "rgba(255, 255, 255, 0.05)",
          borderRadius: 16,
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        }),
        overflow: "visible",
      }}
    >
      {/* Title */}
      {showTitle && (
        <div
          style={{
            position: "absolute",
            left: 24,
            top: 12,
            fontWeight: 400,
            fontSize: "2rem",
            color: "#fff",
            zIndex: 10,
            letterSpacing: 0.5,
            fontFamily: "Calibri, Calibri Light, Segoe UI, sans-serif",
          }}
        >
          {title}
        </div>
      )}

      {/* Info Row: Current Price + Price Range */}
      {((!loading && currentPrice) ||
        (!loading && graphData && graphData.length > 0)) && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 24,
            position: "absolute",
            left: 20,
            top: 72,
            zIndex: 10,
          }}
        >
          {/* Current Price Box */}
          {!loading && currentPrice && (
            <div
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                borderRadius: "8px",
                padding: "12px 16px",
                minWidth: "140px",
                height: "105px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <div
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: "600",
                    color: "#9CA3AF",
                    fontFamily: "Calibri, Calibri Light, Segoe UI, sans-serif",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Current Price
                </div>
                <div
                  style={{
                    fontSize: "1.4rem",
                    fontWeight: "700",
                    color: "#fff",
                    fontFamily: "Calibri, Calibri Light, Segoe UI, sans-serif",
                  }}
                >
                  ${currentPrice.toFixed(2)}
                </div>
                <div
                  style={{
                    fontSize: "0.9rem",
                    fontWeight: "600",
                    color: isPositive ? "#22C55E" : "#EF4444",
                    fontFamily: "Calibri, Calibri Light, Segoe UI, sans-serif",
                  }}
                >
                  {`${isPositive ? "+" : ""}${priceChangePercent.toFixed(2)}%`}
                </div>
              </div>
            </div>
          )}

          {/* Price Range Box */}
          {!loading && graphData && graphData.length > 0 && (
            <div
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                borderRadius: "8px",
                padding: "12px 16px",
                minWidth: "280px",
                height: "105px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <div
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: "600",
                    color: "#9CA3AF",
                    fontFamily: "Calibri, Calibri Light, Segoe UI, sans-serif",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Price Range
                </div>
                {/* Min and Max Labels */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: "500",
                      color: "#9CA3AF",
                      fontFamily:
                        "Calibri, Calibri Light, Segoe UI, sans-serif",
                    }}
                  >
                    ${Math.min(...graphData.map((d) => d.value)).toFixed(2)}
                  </div>
                  <div
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: "500",
                      color: "#9CA3AF",
                      fontFamily:
                        "Calibri, Calibri Light, Segoe UI, sans-serif",
                    }}
                  >
                    ${Math.max(...graphData.map((d) => d.value)).toFixed(2)}
                  </div>
                </div>
              </div>
              {/* Slider */}
              <div style={{ position: "relative", height: "6px" }}>
                {/* Background track */}
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: 0,
                    right: 0,
                    height: "2px",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    transform: "translateY(-50%)",
                    borderRadius: "1px",
                  }}
                />
                {/* Current price indicator */}
                {(() => {
                  const minPrice = Math.min(...graphData.map((d) => d.value));
                  const maxPrice = Math.max(...graphData.map((d) => d.value));
                  const priceRange = maxPrice - minPrice;
                  const currentPosition =
                    ((currentPrice - minPrice) / priceRange) * 100;
                  return (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: `${Math.max(0, Math.min(100, currentPosition))}%`,
                        width: "8px",
                        height: "8px",
                        backgroundColor: "#3B82F6",
                        borderRadius: "50%",
                        transform: "translate(-50%, -50%)",
                        boxShadow: "0 0 8px rgba(59, 130, 246, 0.6)",
                      }}
                    />
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Timeframe buttons */}
      {showTimeframes && (
        <div
          style={{
            position: "absolute",
            right: 32,
            top: 32,
            zIndex: 10,
            width: "300px", // Increased by 10px from 290px
            overflow: "hidden",
            cursor: "pointer",
          }}
          onMouseMove={handleButtonContainerMouseMove}
        >
          <div
            style={{
              display: "flex",
              gap: 8,
              transform: getButtonTransform(),
              transition: "transform 0.15s ease-out",
            }}
          >
            {timeframes.map((tf) => (
            <button
              key={tf.value}
              onClick={() => setTimeframe(tf.value)}
              style={{
                padding: "6px 16px",
                borderRadius: 6,
                border: timeframe === tf.value ? "1px solid #22D3EE" : "none",
                fontWeight: 400,
                fontSize: "0.9rem",
                width: "60px",
                textAlign: "center",
                fontFamily: "Calibri, Calibri Light, Segoe UI, sans-serif",
                background:
                  timeframe === tf.value ? "#3B82F6" : "rgba(255,255,255,0.08)",
                color: timeframe === tf.value ? "#fff" : "#E5E7EB",
                cursor: "pointer",
                boxShadow:
                  timeframe === tf.value 
                    ? "0 2px 12px rgba(34, 211, 238, 0.3), 0 0 0 1px rgba(34, 211, 238, 0.1)" 
                    : "none",
                transition: "all 0.2s",
              }}
            >
              {tf.label}
            </button>
          ))}
          </div>
        </div>
      )}

      {/* Graph content */}
      <div
        style={{
          position: "absolute",
          top: 120,
          left: 0,
          right: 0,
          bottom: showDisplayMode ? 60 : 0, // Make room for display mode buttons
          padding: "0 32px 32px 32px",
          overflow: "hidden",
        }}
      >
        {renderGraph()}

        {/* Loading state */}
        {loading && (
          <div style={{ textAlign: "center", padding: 40, color: "#E5E7EB" }}>
            Loading {title} data...
          </div>
        )}

        {/* Error state */}
        {!loading && (!graphData || graphData.length === 0) && (
          <div style={{ textAlign: "center", padding: 40, color: "#ef4444" }}>
            Failed to load {title} data.
          </div>
        )}
      </div>

      {/* Display mode buttons */}
      {showDisplayMode && (
        <div
          style={{
            position: "absolute",
            left: 32,
            right: 32,
            bottom: 20,
            display: "flex",
            justifyContent: "center",
            gap: 16,
            zIndex: 10,
          }}
        >
          <button
            onClick={() => setDisplayMode("value")}
            style={{
              padding: "10px 0",
              minWidth: 120,
              borderRadius: 8,
              border: "none",
              fontWeight: 700,
              fontSize: "1rem",
              fontFamily: "Calibri, Calibri Light, Segoe UI, sans-serif",
              background:
                displayMode === "value" ? "#3B82F6" : "rgba(255,255,255,0.08)",
              color: displayMode === "value" ? "#fff" : "#E5E7EB",
              cursor: "pointer",
              boxShadow:
                displayMode === "value" ? "0 2px 8px #3B82F633" : "none",
              transition: "all 0.2s",
            }}
          >
            Value
          </button>
          <button
            onClick={() => setDisplayMode("percent")}
            style={{
              padding: "10px 0",
              minWidth: 120,
              borderRadius: 8,
              border: "none",
              fontWeight: 700,
              fontSize: "1rem",
              fontFamily: "Calibri, Calibri Light, Segoe UI, sans-serif",
              background:
                displayMode === "percent"
                  ? "#3B82F6"
                  : "rgba(255,255,255,0.08)",
              color: displayMode === "percent" ? "#fff" : "#E5E7EB",
              cursor: "pointer",
              boxShadow:
                displayMode === "percent" ? "0 2px 8px #3B82F633" : "none",
              transition: "all 0.2s",
            }}
          >
            % Change
          </button>
        </div>
      )}
    </div>
  );
};

export default PortfolioGraph;
